import { CounterPill, Icon } from '@aon/aon-tws-common-ui';
import { IconActionButton, IconType } from '@aon/aon-tws-common-ui/build/lib/components/Common';
import globalVars from '@aon/aon-tws-common-ui/build/lib/globalVars.module.scss';
import _ from 'lodash';
import * as React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import ContentConstants from '../../../contentConstants';
import { ActionType, ConditionSourceType, IDisplayLogicCondition } from '../../../models';
import { IApplicationState } from '../../../store';
import { selectDisplayLogicQuestions } from '../../../store/QuestionBranchingLogicSlice';
import { BranchingLogicHelper } from '../../../utilities';
import { assertNonNullable } from '../../../utilities/assertions';

const ContainerDiv = styled.div.attrs({
	className: 'tws-logic-condition',
})`
	border-radius: 2px;
	margin: 10px;
	margin-left: 20px;
	flex: 1 1 1%;
	-webkit-flex: 1 1 1%;
	-ms-flex: 1 1 1%;
	display: flex;
`;
ContainerDiv.displayName = 'ContainerDiv';

const IfContainer = styled.div.attrs({
	className: 'tws-logic-condition-if',
})`
	flex: 1 1 14%;
	-webkit-flex: 1 1 14%;
	-ms-flex: 1 1 14%;
	display: flex;
	margin-top: 8px;
`;
IfContainer.displayName = 'IfContainer';

const IsContainer = styled.div.attrs({
	className: 'tws-logic-condition-is',
})`
	flex: 1 1 8%;
	-webkit-flex: 1 1 8%;
	-ms-flex: 1 1 8%;
	display: flex;
	margin-top: 8px;
`;
IsContainer.displayName = 'IsContainer';

const QuestionContainer = styled.div.attrs({
	className: 'tws-logic-condition-question',
})`
	flex: 1 1 45%;
	-webkit-flex: 1 1 45%;
	-ms-flex: 1 1 45%;
	display: flex;
`;
QuestionContainer.displayName = 'QuestionContainer';

const QuestionTextContainer = styled.div.attrs({
	className: 'tws-logic-condition-question-value',
})`
	background-color: ${globalVars.mainGray};
	border-radius: ${globalVars.borderRadius2};
	color: ${globalVars.tertiaryGray3};
	font-size: ${globalVars.fontSizeXsmall};
	border-radius: 2px;
	margin-top: 2px;
`;
QuestionTextContainer.displayName = 'QuestionTextContainer';

const ValuesContainer = styled.div.attrs({
	className: 'tws-logic-condition-values',
})`
	flex: 1 1 20%;
	-webkit-flex: 1 1 20%;
	-ms-flex: 1 1 20%;
	display: flex;
`;
ValuesContainer.displayName = 'ValuesContainer';

const ValueContainer = styled.div.attrs({
	className: 'tws-logic-condition-value',
})`
	background-color: ${globalVars.mainGray};
	border-radius: ${globalVars.borderRadius2};
	color: ${globalVars.tertiaryGray3};
	font-size: ${globalVars.fontSizeXsmall};
	border-radius: 2px;
	width: auto;
	margin-top: 2px;
`;
ValueContainer.displayName = 'ValueContainer';

const ValueCounter = styled.div.attrs({
	className: 'tws-summary-item-value-counter',
})`
	display: ${globalVars.displayInlineBlock};
	.tws-counter-pill {
		height: 18px;
		padding: 0 5px;
		line-height: 18px;
		margin-left: 8px;
	}
`;
ValueCounter.displayName = 'ValueCounter';

const RuleAndEditContainer = styled.div.attrs({
	className: 'tws-rule-value',
})`
	font-weight: ${globalVars.fontWeightBold};
	border-radius: 2px;
	min-width: 55px;
	width: 55px;
	margin-top: 16px;
	flex: 1 1 1%;
	-webkit-flex: 1 1 1%;
	-ms-flex: 1 1 1%;
	display: inline-block;

	.tws-edit-handle {
		cursor: pointer;
		margin-right: 0px;
		margin-left: 6px;
		border: 0px;
		font-size: 14px;
	}
`;
RuleAndEditContainer.displayName = 'RuleAndEditContainer';

const ConditionContainer = styled.div.attrs({
	className: 'tws-summary-item',
})`
	overflow-wrap: break-word;
	flex: 1 1 88%;
	-webkit-flex: 1 1 88%;
	-ms-flex: 1 1 88%;
	display: flex;

	> div,
	div > div {
		overflow-wrap: normal;
		padding: 8px;
		display: block;
		text-overflow: ellipsis;
		overflow: hidden;
	}

	.tws-logic-condition-value {
		white-space: nowrap;
	}

	.tws-summary-item-value-counter {
		display: inline-block;
		overflow-wrap: unset;
		padding: unset;
		text-overflow: unset;
		overflow: unset;

		.tws-counter-pill {
			height: 18px;
			padding: 0px 5px;
			line-height: 18px;
			margin-left: 8px;
		}

		> div,
		div > div {
			overflow-wrap: unset;
			padding: unset;
			text-overflow: unset;
			overflow: unset;
		}
	}

	@media (max-width: 1400px) {
		display: block;
	}
`;
ConditionContainer.displayName = 'ConditionContainer';

export interface IQuestionDisplayLogicDrawerItemProps {
	surveyQuestionId: number;
	condition: IDisplayLogicCondition;
	actionType: ActionType;
	showRule: boolean;
	destinationQuestionId?: number;
	ToggleDisplayLogicModal?: (isOpen: boolean, surveyQuestionId: number) => void;
}

export const QuestionDisplayLogicDrawerCondition = ({
	condition,
	actionType,
	showRule,
	surveyQuestionId,
	destinationQuestionId,
	ToggleDisplayLogicModal,
}: IQuestionDisplayLogicDrawerItemProps) => {
	const isBranchQuestion = showRule && actionType === ActionType.BranchToQuestion;

	const displayLogicQuestions = useSelector((state: IApplicationState) => selectDisplayLogicQuestions(state, {}));

	const getIfText = () => {
		if (isBranchQuestion) return ContentConstants.DISPLAY_LOGIC_TO;

		switch (condition.conditionSourceType) {
			case ConditionSourceType.FileAttribute:
			case ConditionSourceType.HrisAttribute:
				return ContentConstants.DISPLAY_LOGIC_IF_ATTRIBUTE;
			case ConditionSourceType.HierarchyNode:
				return ContentConstants.DISPLAY_LOGIC_IF_HIERARCHY;
			case ConditionSourceType.PreviousQuestion:
			default:
				return ContentConstants.DISPLAY_LOGIC_IF_PREVIOUS_QUESTION;
		}
	};

	const getDisplayQuestionText = () => {
		const sourceQuestionId: string | number | undefined = isBranchQuestion ? destinationQuestionId : condition.conditionSourceId;
		let returnText = condition.conditionSourceText;
		if (condition.conditionSourceType === ConditionSourceType.PreviousQuestion || isBranchQuestion) {
			assertNonNullable(sourceQuestionId);
			const selectedQuestion = displayLogicQuestions.find((q) => q.surveyQuestionId === +sourceQuestionId);
			assertNonNullable(selectedQuestion);
			returnText = `Page ${selectedQuestion.pageNumber}: Q${selectedQuestion.displayOrder} ${selectedQuestion.text}`;
		}

		return returnText;
	};

	const getIsText = () => {
		if (isBranchQuestion) return '';

		return condition.conditionSourceType === ConditionSourceType.HierarchyNode
			? ContentConstants.DISPLAY_LOGIC_AND_NODES
			: ContentConstants.DISPLAY_LOGIC_IS;
	};

	const displayText = getDisplayQuestionText();

	return (
		<ContainerDiv>
			<RuleAndEditContainer>
				{showRule &&
					{
						[ActionType.HideQuestion]: ContentConstants.DISPLAY_LOGIC_ACTION_TYPE_HIDE_QUESTION_LABEL,
						[ActionType.ShowQuestion]: ContentConstants.DISPLAY_LOGIC_ACTION_TYPE_SHOW_QUESTION_LABEL,
						[ActionType.BranchToQuestion]: ContentConstants.DISPLAY_LOGIC_ACTION_TYPE_BRANCH_TO_QUESTION_LABEL,
					}[actionType]}
			</RuleAndEditContainer>
			<ConditionContainer>
				<IfContainer>{getIfText()}</IfContainer>
				<QuestionContainer>
					<QuestionTextContainer title={displayText}>{displayText}</QuestionTextContainer>
				</QuestionContainer>
				<IsContainer>{getIsText()}</IsContainer>
				<ValuesContainer>{renderValues()}</ValuesContainer>
			</ConditionContainer>
			<RuleAndEditContainer>
				{showRule && ToggleDisplayLogicModal && (
					<IconActionButton
						label={`Edit Condition ${condition.index + 1}`}
						onClick={() => ToggleDisplayLogicModal(true, surveyQuestionId)}
						className='tws-edit-handle'
					>
						<Icon name={IconType.edit} title={'edit'} />
					</IconActionButton>
				)}
			</RuleAndEditContainer>
		</ContainerDiv>
	);

	function renderValues() {
		if (isBranchQuestion) return '';

		switch (condition.conditionSourceType) {
			case ConditionSourceType.PreviousQuestion:
			case ConditionSourceType.FileAttribute:
			case ConditionSourceType.HrisAttribute:
				return condition.displayLogicValues.map((v, index) => (
					<ValueContainer key={index} title={v.valueText}>
						{v.valueText}
					</ValueContainer>
				));
			case ConditionSourceType.HierarchyNode: {
				const selectedNodes: { displayText: string; count: number }[] = _.flatten(
					condition.displayLogicValues.map((v) => BranchingLogicHelper.getNodeValues(v.valueText)),
				);

				return selectedNodes.map((v, index) => (
					<ValueContainer key={index} title={`${v.displayText} - ${v.count}`}>
						{v.displayText}
						<ValueCounter>
							<CounterPill actualCount={v.count} />
						</ValueCounter>
					</ValueContainer>
				));
			}
			default:
				return <></>;
		}
	}
};
